<template>
    <vx-modal v-model:visible="visible" size="md">
        <template v-slot:header>Suspend Sub Teams</template>

        <div class="space-y-4">
            <vx-label :id="reasonLabelId" size="md">Why are the sub teams being suspended?</vx-label>
            <vx-radio-selection size="sm" v-model="selectedReason" :aria-labelledby="reasonLabelId">
                <vx-radio-selection-item value="Invalid Payment">Invalid Payment</vx-radio-selection-item>
                <vx-radio-selection-item value="Churn">Churn</vx-radio-selection-item>
                <vx-radio-selection-item value="custom">Custom</vx-radio-selection-item>
            </vx-radio-selection>

            <div v-if="selectedReason === 'custom'" class="mt-4">
                <vx-label size="md">Custom Reason</vx-label>
                <vx-input
                    v-model="reason"
                    size="lg"
                    :rows="4"
                    maxlength="250"
                    data-test="suspend-reason-input"
                ></vx-input>
            </div>

            <vx-button
                @click="handleSuspend"
                :loading="submitting"
                :disabled="!reason"
                size="lg"
                color="danger"
                class="w-full"
                data-test="suspend-sub-teams-button"
            >
                Suspend Sub Teams
            </vx-button>

            <div class="text-xs text-gray-500 text-center">
                All active and pending sub teams will be suspended. This team itself will not be suspended.
            </div>
        </div>
    </vx-modal>
</template>

<script setup>
import {
    VxButton,
    VxInput,
    VxLabel,
    VxModal,
    VxRadioSelection,
    VxRadioSelectionItem,
} from '@voxie/frontend-components';
import { ref, useId, watch } from 'vue';
import { HTTP_INTERNAL_SERVER_ERROR } from '~/support/HttpStatuses';
import { useToasted } from '~/composables/useToasted';
import { adminTeamsClient } from '~/services';

const props = defineProps({
    teamId: {
        type: Number,
        required: true,
    },
});

const emit = defineEmits(['suspended']);

const visible = defineModel('visible', {
    type: Boolean,
    default: false,
});

const toasted = useToasted();

const selectedReason = ref('');
const reason = ref('');
const submitting = ref(false);
const reasonLabelId = useId();

watch(selectedReason, () => {
    reason.value = selectedReason.value === 'custom' ? '' : selectedReason.value;
});

watch(visible, (newValue) => {
    if (!newValue) {
        selectedReason.value = '';
        reason.value = '';
    }
});

const handleSuspend = async () => {
    if (submitting.value || !reason.value) {
        return;
    }

    submitting.value = true;

    try {
        await adminTeamsClient.suspendSubTeams(props.teamId, { reason: reason.value });
        toasted.success('Sub teams suspended successfully');
        emit('suspended');
        visible.value = false;
    } catch (e) {
        console.error(e);
        if (e?.response?.status >= HTTP_INTERNAL_SERVER_ERROR) {
            toasted.global.platform_error();
            return;
        }
        toasted.error(e.response?.data?.message || 'Something went wrong.');
    } finally {
        submitting.value = false;
    }
};
</script>
